import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Draggable from 'react-draggable';
import { Box, IconButton, Switch, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { glassSurface, glassText } from '../utils/glassSurface';
import { TEXT_SCALES, setTextScale, useFontSizer, useTextScale }
  from '../utils/analyticaTextScale';
import { describeCorrelation, fixed, pct } from '../utils/analyticaStats';
import {
  CHART_ANALYTICA_ACCENT, CHART_ANALYTICA_DOWN, CHART_ANALYTICA_UP,
} from '../utils/analyticaColors';
import { DIMENSIONS } from '../utils/chartAnalytica';
import { guidanceFor, metricsFor } from '../utils/analyticaGuidance';
import { headline, narrate } from '../utils/analyticaNarrative';

/**
 * THE CHART ANALYTICA DASHBOARD — modeless, draggable, and able to hold several runs at once.
 *
 * <h2>Modeless is a REQUIREMENT, not a style</h2>
 *
 * The whole point of the feature is to run it again on a different bar and compare. A modal
 * would make that a sequence of forgettings: close, click, reopen, and hold the previous
 * numbers in your head. So this is a plain fixed-position panel rather than a MUI `Dialog` —
 * no backdrop, no focus trap, no portal that steals the next click on the chart. The chart stays
 * fully interactive underneath, which is what lets a reader right-click a second bar with the
 * panel still open.
 *
 * <h2>The run history is the comparison</h2>
 *
 * Every run is kept. The selector switches between them and COMPARE puts two side by side in
 * the same tab, so the question "what changes between 2008 and 2020" is answered by reading
 * across rather than by remembering.
 *
 * <h2>Every unavailable dimension says WHY</h2>
 *
 * Six of the fifteen need a drawn cycle and six need a CTS scan. A reader who has neither sees
 * the reason and the gesture that fixes it, not an empty table — an empty table from an
 * analytical engine reads as "measured, and found nothing", which is the opposite of the truth.
 */

const PANEL_WIDTH = 760;
const COMPARE_WIDTH = 1180;

const TABS = [
  // SUMMARY FIRST, and that is the decision rather than the ordering.
  //
  // It is what a reader wants on the way in — the tables answer "what exactly", and the
  // summary answers "so what", which is the question somebody opening a fifteen-dimension
  // panel has first. Putting it last would make it the thing nobody scrolls to.
  { key: 'summary', label: 'Summary' },
  { key: 'overview', label: 'Overview' },
  { key: 'Channel', label: 'Channel' },
  { key: 'CTS', label: 'CTS' },
  { key: 'Structure', label: 'Structure' },
  { key: 'Synthesis', label: 'Synthesis' },
];

/* ───────────────────────────────── presentation primitives ────────────────────────────────── */

const mono = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontVariantNumeric: 'tabular-nums',
};

/**
 * Whether the "How to read this" blocks are expanded.
 *
 * <p>A CONTEXT rather than a prop, because `Section` is used in two dozen places across four
 * tabs and several of them are two components deep. Threading a boolean through all of them
 * would be two dozen signatures and two dozen chances to miss one — and a section left behind
 * would be a panel where some explanations opened and some did not, which reads as a bug in the
 * explanations rather than in the plumbing.
 */
const GuidanceContext = React.createContext(false);

/**
 * HOW TO READ THIS METRIC — the collapsible teaching block under a dimension's heading.
 *
 * <p>Rendered only when the master switch is on, so the panel is a reference by default and an
 * interactive guide on request. A section with no guidance entry renders nothing rather than an
 * empty box: silence is the honest answer to "there is nothing extra to say here".
 */
function HowToRead({ id }) {
  const fs = useFontSizer();
  const open = React.useContext(GuidanceContext);
  const g = guidanceFor(id);
  if (!open || !g) return null;
  const metrics = metricsFor(id);

  const row = (label, body, colour) => (
    <Box sx={{ display: 'flex', gap: 1, mb: 0.4 }}>
      <Typography sx={{ fontSize: fs(9.5), fontWeight: 800, letterSpacing: 0.5,
        color: colour, minWidth: fs(62), flexShrink: 0, pt: '1px' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: fs(10.5), color: glassText(0.75), lineHeight: 1.5 }}>
        {body}
      </Typography>
    </Box>
  );

  return (
    <Box
      data-testid={`analytica-guidance-${id}`}
      sx={{
        mb: 1, p: 1, borderRadius: 1,
        background: 'rgba(0,0,0,0.22)',
        borderLeft: `2px solid ${CHART_ANALYTICA_ACCENT}`,
      }}
    >
      <Typography sx={{ fontSize: fs(9), fontWeight: 800, letterSpacing: 0.8,
        textTransform: 'uppercase', color: CHART_ANALYTICA_ACCENT, mb: 0.6 }}>
        How to read this
      </Typography>
      {row('READING', g.how, glassText(0.55))}
      {row('IN A TRADE', g.practical, glassText(0.55))}
      {/* The trap is the field this dashboard most needs, so it is the one that is coloured. */}
      {row('THE TRAP', g.trap, CHART_ANALYTICA_DOWN)}
      {metrics.length > 0 && (
        <Box sx={{ mt: 0.8, pt: 0.6, borderTop: `1px solid ${glassText(0.1)}` }}>
          {metrics.map(m => (
            <Box key={m.term} sx={{ mb: 0.5 }}>
              <Typography sx={{ fontSize: fs(9.5), fontWeight: 700, color: glassText(0.85) }}>
                {m.term}
              </Typography>
              <Typography sx={{ fontSize: fs(10), color: glassText(0.65), lineHeight: 1.5 }}>
                {m.how} <b>{m.practical}</b>{' '}
                <span style={{ color: CHART_ANALYTICA_DOWN }}>Trap: {m.trap}</span>
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
HowToRead.propTypes = { id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) };

function Section({ id, title, subtitle, children }) {
  const fs = useFontSizer();
  return (
    <Box data-testid={`analytica-section-${id}`} sx={{ mb: 2.5 }}>
      <Typography sx={{
        fontSize: fs(11), fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase',
        color: glassText(0.95), mb: 0.25,
      }}>
        {typeof id === 'number' ? `${id}. ` : ''}{title}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontSize: fs(11), color: glassText(0.55), mb: 0.75, lineHeight: 1.45 }}>
          {subtitle}
        </Typography>
      )}
      {/* ABOVE the tables, so a reader who opened the guide reads the explanation before the
        * numbers rather than scrolling back up to it after being misled by them. */}
      <HowToRead id={id} />
      {children}
    </Box>
  );
}
Section.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.node,
  children: PropTypes.node,
};

/** A dimension that could not run. The REASON is the content — never an empty table. */
function Unavailable({ reason }) {
  const fs = useFontSizer();
  return (
    <Box sx={{
      fontSize: fs(11.5), color: glassText(0.6), border: `1px dashed ${glassText(0.18)}`,
      borderRadius: 1, p: 1, lineHeight: 1.5,
    }}>
      {reason}
    </Box>
  );
}
Unavailable.propTypes = { reason: PropTypes.string.isRequired };

function Cards({ items }) {
  const fs = useFontSizer();
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      gap: 1, mb: 1.5 }}>
      {items.map(item => (
        <Box key={item.label} sx={{
          background: glassText(0.05), border: `1px solid ${glassText(0.1)}`,
          borderRadius: 1, px: 1, py: 0.75,
        }}>
          <Typography sx={{ fontSize: fs(9.5), letterSpacing: 0.6, textTransform: 'uppercase',
            color: glassText(0.5) }}>
            {item.label}
          </Typography>
          <Typography sx={{ ...mono, fontSize: fs(15), fontWeight: 700,
            color: item.color || glassText(1) }}>
            {item.value}
          </Typography>
          {item.hint && (
            <Typography sx={{ fontSize: fs(9.5), color: glassText(0.45) }}>{item.hint}</Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}
Cards.propTypes = { items: PropTypes.array.isRequired };

function Table({ head, rows, align = [] }) {
  const fs = useFontSizer();
  if (!rows || rows.length === 0) {
    return <Typography sx={{ fontSize: fs(11), color: glassText(0.45) }}>No rows.</Typography>;
  }
  const cell = (i) => ({
    ...mono, fontSize: fs(11.5), padding: '3px 8px 3px 0',
    textAlign: align[i] || (i === 0 ? 'left' : 'right'),
    whiteSpace: 'nowrap',
  });
  return (
    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          {head.map((h, i) => (
            <th key={h} style={{ ...cell(i), fontSize: fs(10), fontWeight: 700,
              letterSpacing: 0.4, color: glassText(0.55),
              borderBottom: `1px solid ${glassText(0.14)}`, paddingBottom: 4 }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, ri) => (
          <tr key={r.key || ri}>
            {r.cells.map((c, i) => (
              <td key={i} style={{ ...cell(i),
                color: (r.colors && r.colors[i]) || glassText(0.9),
                borderBottom: `1px solid ${glassText(0.06)}` }}>
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </Box>
  );
}
Table.propTypes = { head: PropTypes.array.isRequired, rows: PropTypes.array,
  align: PropTypes.array };

/** A caveat the reader must carry away with the number above it. */
function Caveat({ children }) {
  const fs = useFontSizer();
  return (
    <Typography sx={{ fontSize: fs(10.5), color: CHART_ANALYTICA_ACCENT, mt: 0.75, lineHeight: 1.5 }}>
      {children}
    </Typography>
  );
}
Caveat.propTypes = { children: PropTypes.node };

/** Green above the baseline, red below, grey when the sample cannot say. */
function verdictColor(verdict) {
  if (verdict === 'above') return CHART_ANALYTICA_UP;
  if (verdict === 'below') return CHART_ANALYTICA_DOWN;
  return glassText(0.55);
}

const signed = (v, decimals = 1) => (v === null || v === undefined || !Number.isFinite(v)
  ? 'n/a' : `${v > 0 ? '+' : ''}${(v * 100).toFixed(decimals)}%`);

/**
 * A relative-volume multiple, or a bare `n/a`.
 *
 * <p>The unit has to be inside the guard. `${fixed(v)}x` renders a missing reading as "n/ax",
 * which is what a chart with no boundary events showed on its first run — a cell that reads as
 * a corrupted number rather than as an absent one.
 */
const multiple = (v) => (Number.isFinite(v) ? `${v.toFixed(2)}x` : 'n/a');

const dimension = (id) => DIMENSIONS.find(d => d.id === id) || { label: '' };

/* ──────────────────────────────────────── the body ────────────────────────────────────────── */

function Overview({ report }) {
  const b = report.bars;
  if (!b) return <Unavailable reason="No bars in range." />;
  return (
    <>
      <Cards items={[
        { label: 'Bars scanned', value: b.bars.toLocaleString() },
        { label: 'Range', value: `${fixed(b.low)} – ${fixed(b.high)}` },
        { label: 'Return', value: signed(b.totalReturnPct / 100),
          color: b.totalReturnPct >= 0 ? CHART_ANALYTICA_UP : CHART_ANALYTICA_DOWN },
        { label: 'Trend', value: b.trend.state === 'UNKNOWN' ? '—' : b.trend.state,
          hint: b.trend.state === 'UNKNOWN' ? 'under 200 bars' : '50 / 200 close' },
        { label: 'Avg volume', value: b.averageVolume === null
          ? 'n/a' : Math.round(b.averageVolume).toLocaleString() },
        { label: 'Sections', value: String(report.inputs.bands) },
        { label: 'CTS points', value: String(report.inputs.ctsPoints) },
        { label: 'Elapsed', value: `${Math.round(report.elapsedMs)} ms` },
      ]} />

      <Section id={1} title={dimension(1).label}
        subtitle={`${b.firstDate} to ${b.lastDate}. The trend state is the 50/200 close `
          + 'crossover — the same pair every macro gate in this application uses.'}>
        <Table
          head={['', 'Value', '', 'Value']}
          rows={[
            { cells: ['Highest high', `${fixed(b.high)} (${b.highDate})`,
              'Up bars', b.upBars.toLocaleString()] },
            { cells: ['Lowest low', `${fixed(b.low)} (${b.lowDate})`,
              'Down bars', b.downBars.toLocaleString()] },
            { cells: ['Range', fixed(b.range), 'Doji bars', b.dojiBars.toLocaleString()] },
            { cells: ['Average bar range', fixed(b.averageRange),
              'Median volume', b.medianVolume === null
                ? 'n/a' : Math.round(b.medianVolume).toLocaleString()] },
          ]}
          align={['left', 'right', 'left', 'right']}
        />
      </Section>

      <Section title="What this scan could see"
        subtitle="Every figure in this dashboard is conditional on these. Two runs that differ
          here differ in SCOPE rather than in outcome.">
        <Table head={['Input', 'State']} rows={[
          { cells: ['PTA / PTB sections',
            report.inputs.bands > 0
              ? `${report.inputs.bands} (${report.inputs.bandCategories.join(', ')})`
              : 'none — no cycle drawn'] },
          { cells: ['CTS points', report.inputs.ctsPoints > 0
            ? String(report.inputs.ctsPoints) : 'none — no timing scan on the chart'] },
          { cells: ['RSI', report.inputs.rsi ? 'available' : 'not available'] },
          { cells: ['Volume', report.inputs.volume ? 'available' : 'not reported'] },
          { cells: ['Forward horizon', `${report.inputs.horizon} bars`] },
          { cells: ['Pivot strength', `${report.inputs.pivotStrength} bars either side`] },
        ]} />
      </Section>
    </>
  );
}
Overview.propTypes = { report: PropTypes.object.isRequired };

function ChannelTab({ report }) {
  const fs = useFontSizer();
  const u = report.unavailable;
  if (u.excursion) return <Unavailable reason={u.excursion} />;
  const band = report.band;
  const hv = band.heightVsExcursion;

  const tierRows = (side) => band.excursion[side].map(t => ({
    key: `${side}-${t.label}`,
    cells: [t.label, String(t.count), pct(t.rate)],
  }));

  const layerRows = (side) => band.boundaries[side].map(l => ({
    key: `${side}-${l.layer}`,
    cells: [l.layer, String(l.penetrations), String(l.reversions), String(l.tests),
      pct(l.hold.rate)],
  }));

  return (
    <>
      <Section id={2} title={dimension(2).label}
        subtitle={`${band.sections} section${band.sections === 1 ? '' : 's'} in scope`
          + `${band.clippedSections > 0
            ? `, ${band.clippedSections} still open at the clicked bar` : ''}.`
          + ' Measured from the channel EDGE, in band heights. A section is counted once however'
          + ' long it stayed out.'}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: fs(10), color: CHART_ANALYTICA_UP, mb: 0.5,
              fontWeight: 700 }}>LONG SIDE (above the top)</Typography>
            <Table head={['Tier', 'n', 'Freq']} rows={tierRows('long')} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: fs(10), color: CHART_ANALYTICA_DOWN, mb: 0.5,
              fontWeight: 700 }}>SHORT SIDE (below the bottom)</Typography>
            <Table head={['Tier', 'n', 'Freq']} rows={tierRows('short')} />
          </Box>
        </Box>
        <Table head={['', 'Long', 'Short']} rows={[
          { cells: ['Reached at least 1x', pct(band.excursion.longAtLeast1x.rate),
            pct(band.excursion.shortAtLeast1x.rate)] },
          { cells: ['Median excursion (heights)', fixed(band.excursion.medianLong),
            fixed(band.excursion.medianShort)] },
        ]} />
      </Section>

      <Section id={3} title={dimension(3).label}
        subtitle="A CLOSE crossing the midline, named by the side price came FROM. Counted as a
          transition, so an excursion that stayed out for forty sessions counts once.">
        <Table head={['Direction', 'Count']} rows={[
          { cells: ['Penetrated upward (from below)', String(band.equator.penetrationUp)] },
          { cells: ['Penetrated downward (from above)', String(band.equator.penetrationDown)] },
          { cells: ['Bars that touched the equator', String(band.equator.touches)] },
          { cells: ['Penetration rate per touch', pct(band.equator.penetrationRate.rate)] },
        ]} />
      </Section>

      <Section id={4} title={dimension(4).label}
        subtitle="A touch of the midline that was REJECTED: the bar's extreme reached it and the
          close finished back on the side it came from.">
        <Table head={['Direction', 'Count']} rows={[
          { cells: ['Held from above (bounced down)', String(band.equator.reversionFromAbove)] },
          { cells: ['Held from below (bounced up)', String(band.equator.reversionFromBelow)] },
          { cells: ['Reversion rate per touch', pct(band.equator.reversionRate.rate)] },
        ]} />
      </Section>

      <Section id={11} title={`${dimension(11).label} / ${dimension(12).label}`}
        subtitle={`b0 is the measured edge, b1 one band height beyond it, b2 and b3 the solid and
          dotted guide lines the chart draws. HOLD is reversions over the bars that TESTED a
          layer — the number worth reading, and it exists because the two are mutually exclusive.`}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: fs(10), color: CHART_ANALYTICA_UP, mb: 0.5,
              fontWeight: 700 }}>UPPER LAYERS</Typography>
            <Table head={['', 'Pen', 'Rev', 'Tests', 'Hold']} rows={layerRows('up')} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: fs(10), color: CHART_ANALYTICA_DOWN, mb: 0.5,
              fontWeight: 700 }}>LOWER LAYERS</Typography>
            <Table head={['', 'Pen', 'Rev', 'Tests', 'Hold']} rows={layerRows('down')} />
          </Box>
        </Box>
      </Section>

      <Section id={8} title={dimension(8).label}
        subtitle="Three correlations, because each alone misleads and in a different direction.">
        <Table head={['Reading', 'r', 'n', 'Strength']} rows={[
          { cells: ['Relative (both as a fraction of price) — READ THIS',
            fixed(hv.relative.r), String(hv.relative.n), describeCorrelation(hv.relative.r)],
          colors: [glassText(1)] },
          { cells: ['Absolute (price units)', fixed(hv.absolute.r), String(hv.absolute.n),
            describeCorrelation(hv.absolute.r)] },
          { cells: ['Normalised (excursion in band heights)', fixed(hv.normalised.r),
            String(hv.normalised.n), describeCorrelation(hv.normalised.r)] },
        ]} />
        <Caveat>
          The ABSOLUTE reading is dominated by the price level on a long chart — a channel and a
          move both grow with price, so r comes back large whether or not a taller band means
          anything. The NORMALISED one divides the move by the very quantity it is correlated
          against, so a negative r there is the null expectation rather than a finding. Only the
          relative reading has the price trend divided out of both sides.
        </Caveat>
        {hv.quartiles.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Table head={['Height quartile', 'n', 'Median height', 'Mean excursion (h)',
              'Mean excursion (%)']}
              rows={hv.quartiles.map(q => ({
                key: q.quartile,
                cells: [`Q${q.quartile}`, String(q.n), pct(q.medianHeightPct),
                  fixed(q.meanExcursionHeights), pct(q.meanExcursionPct)],
              }))} />
          </Box>
        )}
      </Section>
    </>
  );
}
ChannelTab.propTypes = { report: PropTypes.object.isRequired };

function CtsTab({ report }) {
  const fs = useFontSizer();
  const u = report.unavailable;
  if (u.ctsUpside) return <Unavailable reason={u.ctsUpside} />;
  const cts = report.cts;

  const zoneRows = (block) => block.zones.map(z => ({
    key: z.zone,
    cells: [z.zone.replace('_', ' '), String(z.count), String(z.unresolved),
      pct(z.rate), signed(z.edge), z.verdict || '—'],
    colors: [null, null, null, null, verdictColor(z.verdict), verdictColor(z.verdict)],
  }));

  const divRow = (side, label) => {
    const d = cts.withDivergence[side];
    return [
      { key: `${side}-with`, cells: [`${label} · with an agreeing divergence`,
        String(d.with.total), pct(d.with.rate), signed(d.with.edge)],
      colors: [null, null, verdictColor(d.with.verdict), verdictColor(d.with.verdict)] },
      { key: `${side}-without`, cells: [`${label} · without one`,
        String(d.without.total), pct(d.without.rate), signed(d.without.edge)] },
      { key: `${side}-lift`, cells: [`${label} · LIFT from the filter`, '—', '—',
        signed(d.lift)],
      colors: [glassText(1), null, null,
        d.lift > 0 ? CHART_ANALYTICA_UP : CHART_ANALYTICA_DOWN] },
    ];
  };

  return (
    <>
      <Cards items={[
        { label: 'CTS points', value: String(cts.total) },
        { label: 'Unresolved', value: String(cts.unresolved),
          hint: `horizon ${cts.horizon} bars` },
        { label: 'Baseline up', value: pct(cts.baseline.up), hint: `${cts.baseline.n} bars` },
        { label: 'Baseline down', value: pct(cts.baseline.down) },
        { label: 'RSI divergences', value: String(cts.divergenceCount) },
      ]} />
      <Caveat>
        Every rate below is reported against the BASELINE measured on the same bars over the same
        horizon. A 54% success rate on a series that closes higher 60% of the time is worse than
        buying at random, and an EDGE column is the only way to see that. UNRESOLVED signals —
        those whose horizon runs past the clicked bar — are excluded from both halves of every
        rate rather than counted as failures.
      </Caveat>

      <Section id={5} title={dimension(5).label}
        subtitle="Zone 1 is inside the channel, Zone 2 the first extension, Zone 3 beyond it.">
        <Table head={['Zone', 'n', 'Unres.', 'Success', 'Edge', 'vs baseline']}
          rows={zoneRows(cts.upsideByZone)} />
        {cts.upsideByZone.unzoned > 0 && (
          <Typography sx={{ fontSize: fs(10.5), color: glassText(0.5), mt: 0.5 }}>
            {cts.upsideByZone.unzoned} point(s) had no channel in force and belong to no zone.
          </Typography>
        )}
      </Section>

      <Section id={6} title={dimension(6).label}>
        <Table head={['Zone', 'n', 'Unres.', 'Success', 'Edge', 'vs baseline']}
          rows={zoneRows(cts.downsideByZone)} />
      </Section>

      <Section id={7} title={dimension(7).label}
        subtitle={u.ctsDivergence || 'A divergence AGREES when it points the same way as the '
          + 'signal. One of the opposite sense beside a point is a conflict, and counting it '
          + 'would make this measure proximity rather than confluence.'}>
        {u.ctsDivergence ? <Unavailable reason={u.ctsDivergence} /> : (
          <>
            <Table head={['', 'n', 'Success', 'Edge']}
              rows={[...divRow('up', 'Upside'), ...divRow('down', 'Downside')]} />
            <Typography sx={{ fontSize: fs(10.5), color: glassText(0.5), mt: 0.5 }}>
              Concurrence: {pct(cts.withDivergence.up.concurrence.rate)} of upside points and{' '}
              {pct(cts.withDivergence.down.concurrence.rate)} of downside points had an agreeing
              divergence within the window.
            </Typography>
          </>
        )}
      </Section>

      <Section id={9} title={dimension(9).label}
        subtitle="Mean reversion means the close at the horizon is CLOSER to the equator than the
          close at the signal. Reported as buckets as well as a coefficient, because a
          non-monotone relationship is the shape this question most plausibly has.">
        <Table head={['Distance from equator', 'n', 'Reverted', '95% interval']}
          rows={cts.distanceVsReversion.buckets.map(b => ({
            key: b.label,
            cells: [b.label, String(b.total), pct(b.rate),
              b.ci ? `${pct(b.ci.low)} – ${pct(b.ci.high)}` : 'n/a'],
          }))} />
        <Typography sx={{ ...mono, fontSize: fs(11), color: glassText(0.75), mt: 0.75 }}>
          r = {fixed(cts.distanceVsReversion.correlation.r)}
          {' '}({describeCorrelation(cts.distanceVsReversion.correlation.r)}),
          {' '}n = {cts.distanceVsReversion.correlation.n},
          {' '}overall reversion {pct(cts.distanceVsReversion.overall.rate)}
        </Typography>
      </Section>
    </>
  );
}
CtsTab.propTypes = { report: PropTypes.object.isRequired };

function StructureTab({ report }) {
  const fs = useFontSizer();
  const fib = report.fibonacci;
  const vol = report.volume;
  const u = report.unavailable;

  const fibRows = (side) => (fib[side] || []).map(r => ({
    key: `${side}-${r.ratio}`,
    cells: [r.label, String(r.total), pct(r.rate),
      r.ci ? `${pct(r.ci.low)} – ${pct(r.ci.high)}` : 'n/a'],
  }));

  return (
    <>
      <Section id={10} title={dimension(10).label}
        subtitle={`${fib.swings} swing${fib.swings === 1 ? '' : 's'} found`
          + `${fib.openSwings > 0 ? `, ${fib.openSwings} still retracing and excluded` : ''}.`
          + ' A retracement window closes when the impulse makes a new extreme (extended) or'
          + ' price gives back more than 100% (invalidated) — without a terminating condition'
          + ' every swing eventually completes and the dimension measures nothing.'}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: fs(10), color: CHART_ANALYTICA_UP, mb: 0.5,
              fontWeight: 700 }}>BULLISH IMPULSES (retracing down)</Typography>
            <Table head={['Ratio', 'n', 'Completed', '95% interval']} rows={fibRows('bullish')} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: fs(10), color: CHART_ANALYTICA_DOWN, mb: 0.5,
              fontWeight: 700 }}>BEARISH IMPULSES (retracing up)</Typography>
            <Table head={['Ratio', 'n', 'Completed', '95% interval']} rows={fibRows('bearish')} />
          </Box>
        </Box>
        <Typography sx={{ ...mono, fontSize: fs(11), color: glassText(0.75), mt: 0.75 }}>
          Median retracement depth: {pct(fib.medianDepth)}
        </Typography>
      </Section>

      <Section id={14} title={dimension(14).label}
        subtitle="Relative volume is each bar against the twenty before it, EXCLUDING itself —
          a spike that is part of its own denominator understates by exactly the amount that
          matters most.">
        {u.volume ? <Unavailable reason={u.volume} /> : (
          <>
            <Table head={['Measure', 'Value', 'n']} rows={[
              { cells: ['Mean relative volume', multiple(vol.meanRvol),
                String(vol.barsRead)] },
              { cells: ['On boundary PENETRATION bars', multiple(vol.penetrationRvol),
                String(vol.penetrationCount)] },
              { cells: ['On boundary REVERSION bars', multiple(vol.reversionRvol),
                String(vol.reversionCount)] },
              { cells: ['Bars with expansion (≥1.3x)', pct(vol.expansionRate.rate), ''] },
              { cells: ['Bars with a dry-up (≤0.8x)', pct(vol.dryUpRate.rate), ''] },
            ]} />
            <Box sx={{ mt: 1 }}>
              <Table head={['CTS signals', 'n', 'Success', 'Mean rvol']} rows={[
                { cells: ['On expanded volume', String(vol.cts.expanded.n),
                  pct(vol.cts.expanded.rate), multiple(vol.cts.expanded.meanRvol)] },
                { cells: ['On quiet volume', String(vol.cts.quiet.n),
                  pct(vol.cts.quiet.rate), multiple(vol.cts.quiet.meanRvol)] },
                { cells: ['LIFT from a volume filter', '—', signed(vol.cts.lift), ''],
                  colors: [glassText(1), null,
                    vol.cts.lift > 0 ? CHART_ANALYTICA_UP : CHART_ANALYTICA_DOWN] },
              ]} />
            </Box>
            <Typography sx={{ fontSize: fs(11), color: glassText(0.7), mt: 0.75, lineHeight: 1.5 }}>
              {volumeCommentary(vol)}
            </Typography>
          </>
        )}
      </Section>
    </>
  );
}
StructureTab.propTypes = { report: PropTypes.object.isRequired };

/** The actionable sentence dimension 14 exists to produce, built from its own numbers. */
export function volumeCommentary(vol) {
  if (!vol) return '';
  const parts = [];
  const pen = vol.penetrationRvol;
  const rev = vol.reversionRvol;
  if (Number.isFinite(pen) && Number.isFinite(rev)) {
    parts.push(pen > rev * 1.1
      ? `Breakouts through a boundary carried ${fixed(pen)}x volume against ${fixed(rev)}x on `
        + 'the bars that were rejected, so expansion is doing what a breakout filter needs it to.'
      : pen < rev * 0.9
        ? `Breakouts carried LESS volume (${fixed(pen)}x) than the rejections (${fixed(rev)}x) — `
          + 'on this range a volume filter on penetrations would have selected the wrong bars.'
        : `Penetrations and reversions carried much the same volume (${fixed(pen)}x against `
          + `${fixed(rev)}x), so volume did not separate them here.`);
  }
  const lift = vol.cts?.lift;
  if (Number.isFinite(lift)) {
    parts.push(lift > 0.05
      ? `Requiring expansion at a CTS point was worth ${signed(lift)} on this range — a filter `
        + 'worth applying.'
      : lift < -0.05
        ? `Requiring expansion at a CTS point COST ${signed(lift)} here: the quiet signals `
          + 'resolved better, so the filter would have discarded the ones that worked.'
        : 'Volume at the CTS point made no material difference to the outcome on this range.');
  }
  return parts.join(' ');
}

function SynthesisTab({ report }) {
  const fs = useFontSizer();
  const u = report.unavailable;
  const cts = report.cts;
  const edge = report.edge;

  return (
    <>
      <Section id={13} title={dimension(13).label}>
        {u.ctsRsiSynthesis ? <Unavailable reason={u.ctsRsiSynthesis} /> : (
          <>
            <Table head={['Measure', 'Value']} rows={[
              { cells: ['CTS points in scope', String(cts.synthesis.points)] },
              { cells: ['RSI divergences found', String(cts.synthesis.divergences)] },
              { cells: ['Points with an AGREEING divergence', String(cts.synthesis.concurrent)] },
              { cells: ['Points with a CONFLICTING one', String(cts.synthesis.conflicting)] },
              { cells: ['Mean RSI at an upside point', fixed(cts.synthesis.meanRsiUp, 1)] },
              { cells: ['Mean RSI at a downside point', fixed(cts.synthesis.meanRsiDown, 1)] },
              { cells: ['r(RSI reading, success)',
                `${fixed(cts.synthesis.rsiVsSuccess.r)} `
                + `(${describeCorrelation(cts.synthesis.rsiVsSuccess.r)}, `
                + `n = ${cts.synthesis.rsiVsSuccess.n})`] },
            ]} />
            <Typography sx={{ fontSize: fs(11), color: glassText(0.7), mt: 0.75, lineHeight: 1.5 }}>
              {ctsRsiCommentary(cts)}
            </Typography>
          </>
        )}
      </Section>

      <Section id={15} title={dimension(15).label}>
        {u.edge ? <Unavailable reason={u.edge} /> : (
          <>
            <Table head={['Component', 'Measured on this scan']}
              rows={edge.components.map(c => ({
                key: c.key,
                cells: [c.label, c.available ? 'contributing' : 'no measurement — contributes 0'],
                colors: [null, c.available ? CHART_ANALYTICA_UP : glassText(0.45)],
              }))} />
            <Box sx={{ mt: 1 }}>
              <Table head={['Rating', 'n', 'Success', 'Mean return', '95% interval']}
                rows={edge.buckets.map(b => ({
                  key: b.label,
                  cells: [b.label, String(b.n), pct(b.rate),
                    b.meanReturnPct === null ? 'n/a' : `${fixed(b.meanReturnPct)}%`,
                    b.ci ? `${pct(b.ci.low)} – ${pct(b.ci.high)}` : 'n/a'],
                }))} />
            </Box>
            <Typography sx={{ ...mono, fontSize: fs(12), color: glassText(0.95), mt: 1 }}>
              High-vs-Low separation: {signed(edge.separation)}
            </Typography>
            <Caveat>
              IN-SAMPLE. Each component is worth what it was MEASURED to be worth on these very
              points, so the rating is fitted to the trades it then ranks — the separation above
              is arithmetic, not evidence. Use it to RANK setups within this chart, never as a
              probability. If High and Low resolve at the same rate, the rating is ranking
              nothing however carefully it was built.
            </Caveat>
          </>
        )}
      </Section>
    </>
  );
}
SynthesisTab.propTypes = { report: PropTypes.object.isRequired };

/** Dimension 13's sentence, built from its own numbers rather than asserted. */
export function ctsRsiCommentary(cts) {
  if (!cts) return '';
  const s = cts.synthesis;
  const parts = [];
  const rateOf = (pointCount) => (s.points > 0 ? pointCount / s.points : null);
  const conc = rateOf(s.concurrent);
  if (conc !== null) {
    parts.push(conc < 0.1
      ? `Only ${pct(conc)} of CTS points had an agreeing RSI divergence beside them, so on this `
        + 'range the two studies are largely independent — a confluence filter built on them '
        + 'would discard almost everything.'
      : `${pct(conc)} of CTS points carried an agreeing RSI divergence.`);
  }
  const liftUp = cts.withDivergence?.up?.lift;
  const liftDown = cts.withDivergence?.down?.lift;
  const best = [liftUp, liftDown].filter(Number.isFinite);
  if (best.length > 0) {
    const anyPositive = best.some(v => v > 0.05);
    parts.push(anyPositive
      ? 'Where a divergence agreed, the signals resolved better than those without one.'
      : 'A divergence beside a CTS point did not improve its outcome on this range.');
  }
  if (Number.isFinite(s.rsiVsSuccess.r) && Math.abs(s.rsiVsSuccess.r) < 0.2) {
    parts.push('The RSI READING at the signal bar is uncorrelated with the outcome, which says '
      + 'the level is not what carries whatever the divergence does.');
  }
  return parts.join(' ');
}

/* ───────────────────────────────────── the panel ──────────────────────────────────────────── */

/**
 * THE PLAIN-ENGLISH SUMMARY — the dense output, said in sentences.
 *
 * <p>It renders `narrate()` and computes nothing of its own. A second derivation here would be a
 * second answer to a question the engine has already answered carefully, a tab away from the
 * table that disagrees with it.
 *
 * <p>The ACTIONABLE section is coloured by what it found and not by what a reader would like it
 * to find: green only when something cleared its baseline, amber when nothing did. A summary
 * that always reads positively would turn the most carefully hedged panel in this application
 * into its most misleading one — prose is persuasive in a way a table is not.
 */
function SummaryTab({ report }) {
  const fs = useFontSizer();
  const sections = narrate(report);
  const tone = {
    positive: CHART_ANALYTICA_UP,
    caution: CHART_ANALYTICA_ACCENT,
    neutral: glassText(0.9),
  };

  return (
    <Box data-testid="analytica-summary">
      <Box sx={{
        p: 1.25, mb: 2, borderRadius: 1,
        background: glassText(0.05),
        borderLeft: `3px solid ${CHART_ANALYTICA_ACCENT}`,
      }}>
        <Typography sx={{ fontSize: fs(12.5), color: glassText(0.95), lineHeight: 1.6 }}>
          {headline(report)}
        </Typography>
      </Box>

      {sections.map(section => (
        <Box key={section.id} data-testid={`analytica-summary-${section.id}`} sx={{ mb: 2.25 }}>
          <Typography sx={{
            fontSize: fs(11), fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase',
            color: tone[section.tone] || tone.neutral, mb: 0.6,
          }}>
            {section.heading}
          </Typography>
          {section.lines.map((line, i) => (line === '—' ? (
            // A deliberate divider inside a section, for the "worth watching" break. Rendered
            // as a rule rather than as a line of text containing a dash.
            <Box key={`rule-${i}`} sx={{ borderTop: `1px solid ${glassText(0.12)}`,
              my: 1 }} />
          ) : (
            <Typography key={i} sx={{
              fontSize: fs(11.5), color: glassText(0.8), lineHeight: 1.65, mb: 0.8,
            }}>
              {line}
            </Typography>
          )))}
        </Box>
      ))}
    </Box>
  );
}
SummaryTab.propTypes = { report: PropTypes.object.isRequired };

function ReportBody({ report, tab }) {
  if (!report) return null;
  if (!report.ok) return <Unavailable reason={report.reason} />;
  if (tab === 'summary') return <SummaryTab report={report} />;
  if (tab === 'overview') return <Overview report={report} />;
  if (tab === 'Channel') return <ChannelTab report={report} />;
  if (tab === 'CTS') return <CtsTab report={report} />;
  if (tab === 'Structure') return <StructureTab report={report} />;
  return <SynthesisTab report={report} />;
}
ReportBody.propTypes = { report: PropTypes.object, tab: PropTypes.string.isRequired };

export default function ChartAnalyticaDialog({
  open, runs, activeId, onSelectRun, onClose, annotations, onToggleAnnotations,
}) {
  const fs = useFontSizer();
  const scale = useTextScale();
  const [tab, setTab] = useState('summary');
  /**
   * Whether the "How to read this" guidance is expanded.
   *
   * <p>ONE switch for the whole panel rather than a disclosure per section. A reader learning
   * the dashboard wants every explanation at once and a reader who knows it wants none of them;
   * fifteen individual toggles would make that two gestures per section and would leave the
   * panel in a half-explained state nobody chose.
   */
  const [guidance, setGuidance] = useState(false);
  const [compare, setCompare] = useState(false);
  const [compareId, setCompareId] = useState(null);
  // react-draggable reaches for the DOM node; `nodeRef` is what keeps it off findDOMNode, which
  // React 18 warns about in StrictMode.
  const nodeRef = useRef(null);

  const active = useMemo(
    () => runs.find(r => r.id === activeId) || runs[runs.length - 1] || null,
    [runs, activeId],
  );
  const other = useMemo(() => {
    if (!compare) return null;
    const pick = runs.find(r => r.id === compareId);
    if (pick && pick.id !== active?.id) return pick;
    return runs.filter(r => r.id !== active?.id).slice(-1)[0] || null;
  }, [compare, compareId, runs, active]);

  /**
   * AN EMPTIED HISTORY RETURNS THE PANEL TO SUMMARY — which only happens on a symbol change.
   *
   * <p>The tab is otherwise sticky, and should be: a reader running a second scan to compare it
   * against the first wants to land on the same table. But a new SYMBOL flushes every run, and
   * the next scan can easily have nothing for whichever tab was open — the new chart has no CTS
   * study on it yet, so a reader sitting on the CTS tab lands on an "unavailable" message and
   * has no reason to think a summary is one click away. Found by switching symbol with the
   * panel open.
   *
   * <p>Keyed on the history going EMPTY rather than on the symbol, because the panel is not told
   * about symbols — and an empty history is exactly the event that invalidates the tab.
   */
  const hadRuns = useRef(false);
  React.useEffect(() => {
    if (runs.length === 0 && hadRuns.current) {
      setTab('summary');
      setCompare(false);
    }
    hadRuns.current = runs.length > 0;
  }, [runs.length]);

  if (!open) return null;
  const canCompare = runs.length > 1;
  const width = compare && other ? COMPARE_WIDTH : PANEL_WIDTH;

  /*
   * NO `bounds` ON THE DRAGGABLE, and it shipped with `bounds="parent"` for one commit.
   *
   * react-draggable computes a parent bound from the OFFSET PARENT's content box. For a
   * `position: fixed` panel that box has nothing to do with the viewport the panel is
   * positioned against, so the numbers it derives are about the wrong rectangle. Measured: a
   * drag asking for +90px of Y moved the panel by exactly −88 and stopped — which is
   * `-node.offsetTop`, the panel's own CSS `top`. The bound was holding it against a box it has
   * no relationship to, and nothing errored.
   *
   * <p>Unbounded, the panel can in principle be dragged off-screen. Closing and reopening
   * recovers it, because the component unmounts at `open=false` and the CSS top/left apply
   * again — cheaper than a viewport clamp that would have to re-measure the panel on resize.
   */
  return (
    /*
     * CENTRED BY A FLEX WRAPPER, not by a transform — because the transform is taken.
     *
     * The usual way to centre a fixed element of unknown height is `top: 50%` plus
     * `translate(-50%, -50%)`, and react-draggable writes its own `transform` onto the node to
     * move it. The two cannot share the property: the first drag would discard the centring
     * and the panel would jump by half its own size before following the pointer.
     *
     * <p>A wrapper that centres with flexbox leaves the panel's transform free, so a drag is a
     * clean offset FROM centre. The wrapper is `pointer-events: none` and the panel restores
     * `auto` — without that the full-viewport wrapper would swallow every click on the chart,
     * which would undo the one property this dialog exists to have.
     */
    <Box
      data-testid="chart-analytica-centering"
      sx={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        zIndex: 1350,
      }}
    >
      <Draggable handle=".analytica-drag" nodeRef={nodeRef}>
      <Box
        ref={nodeRef}
        data-testid="chart-analytica-dialog"
        sx={{
          ...glassSurface(),
          position: 'relative',
          pointerEvents: 'auto',
          width,
          maxWidth: 'calc(100vw - 48px)',
          maxHeight: 'calc(100vh - 140px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* ── the handle, which is also the header ─────────────────────────────────────── */}
        <Box className="analytica-drag" sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 1,
          borderBottom: `1px solid ${glassText(0.12)}`, cursor: 'move', flex: '0 0 auto',
        }}>
          <DragIndicatorIcon sx={{ fontSize: fs(18), color: glassText(0.35) }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: fs(14), fontWeight: 800, letterSpacing: 0.4,
              color: glassText(1), lineHeight: 1.2 }}>
              Chart Analytica
            </Typography>
            <Typography noWrap sx={{ ...mono, fontSize: fs(10.5), color: glassText(0.6) }}>
              {active
                ? `${active.report.symbol || '—'} · bar 0 → ${active.report.clickedDate}`
                  + ` · ${(active.report.scannedBars || 0).toLocaleString()} bars`
                  + ` · ${Math.round(active.report.elapsedMs)} ms`
                : 'No run yet'}
            </Typography>
          </Box>
          <Tooltip title={canCompare
            ? 'Compare two runs side by side'
            : 'Run the analysis on a second bar to compare'}>
            <span>
              <IconButton
                size="small"
                disabled={!canCompare}
                aria-label="Compare runs"
                aria-pressed={compare}
                onClick={() => setCompare(v => !v)}
                sx={{ color: compare ? CHART_ANALYTICA_ACCENT : glassText(0.7),
                  '&.Mui-disabled': { color: glassText(0.25) } }}
              >
                <CompareArrowsIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton size="small" aria-label="Close Chart Analytica" onClick={onClose}
            sx={{ color: glassText(0.7) }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* ── the run history and the annotation master switch ─────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.75,
          borderBottom: `1px solid ${glassText(0.08)}`, flexWrap: 'wrap', flex: '0 0 auto' }}>
          <Typography sx={{ fontSize: fs(10), letterSpacing: 0.5, color: glassText(0.5) }}>
            RUNS
          </Typography>
          <Box component="select"
            data-testid="analytica-run-select"
            aria-label="Select a Chart Analytica run"
            value={active?.id || ''}
            onChange={(e) => onSelectRun(e.target.value)}
            sx={{ ...mono, fontSize: fs(11), background: 'rgba(0,0,0,0.35)', color: glassText(0.95),
              border: `1px solid ${glassText(0.18)}`, borderRadius: 1, px: 0.75, py: 0.25 }}
          >
            {runs.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
          </Box>
          {compare && other && (
            <>
              <Typography sx={{ fontSize: fs(10), color: glassText(0.5) }}>AGAINST</Typography>
              <Box component="select"
                data-testid="analytica-compare-select"
                aria-label="Select a run to compare against"
                value={other.id}
                onChange={(e) => setCompareId(e.target.value)}
                sx={{ ...mono, fontSize: fs(11), background: 'rgba(0,0,0,0.35)',
                  color: glassText(0.95), border: `1px solid ${glassText(0.18)}`,
                  borderRadius: 1, px: 0.75, py: 0.25 }}
              >
                {runs.filter(r => r.id !== active?.id)
                  .map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
              </Box>
            </>
          )}
          <Box sx={{ flex: 1 }} />

          {/* ── TEXT SIZE ──────────────────────────────────────────────────────────────────
            * Three buttons rather than a dropdown: it is a two-click adjustment a reader makes
            * while looking at the thing being adjusted, and a select would hide the other two
            * options behind a menu that covers the text they are judging.
            */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}
            role="group" aria-label="Text size">
            <Typography sx={{ fontSize: fs(10), letterSpacing: 0.5, color: glassText(0.5),
              mr: 0.25 }}>
              TEXT
            </Typography>
            {TEXT_SCALES.map(s => (
              <Tooltip key={s.key} title={s.hint}>
                <Box
                  component="button"
                  type="button"
                  data-testid={`analytica-text-${s.key}`}
                  aria-label={`${s.key} text`}
                  aria-pressed={scale === s.key}
                  onClick={() => setTextScale(s.key)}
                  sx={{
                    ...mono, cursor: 'pointer', lineHeight: 1,
                    // The button itself scales, so the control demonstrates what it does.
                    fontSize: fs(10), fontWeight: 700,
                    px: 0.65, py: 0.3, borderRadius: 0.75,
                    color: scale === s.key ? '#111' : glassText(0.7),
                    background: scale === s.key ? CHART_ANALYTICA_ACCENT : 'rgba(0,0,0,0.3)',
                    border: `1px solid ${scale === s.key
                      ? CHART_ANALYTICA_ACCENT : glassText(0.18)}`,
                  }}
                >
                  {s.label}
                </Box>
              </Tooltip>
            ))}
          </Box>

          <Tooltip title="Expand a 'How to read this' block under every dimension — what the
            numbers mean in a trade, and the misreading to avoid.">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Switch
                size="small"
                checked={guidance}
                onChange={(e) => setGuidance(e.target.checked)}
                inputProps={{ 'aria-label': 'Show how to read each metric' }}
              />
              <Typography sx={{ fontSize: fs(10.5), color: glassText(0.7) }}>Guide</Typography>
            </Box>
          </Tooltip>

          <Tooltip title="Show the event markers and audit verticals on the chart. The scanned
            range and the clicked bar stay marked either way.">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Switch
                size="small"
                checked={annotations}
                onChange={(e) => onToggleAnnotations(e.target.checked)}
                inputProps={{ 'aria-label': 'Show chart annotations' }}
              />
              <Typography sx={{ fontSize: fs(10.5), color: glassText(0.7) }}>Annotations</Typography>
            </Box>
          </Tooltip>
        </Box>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            minHeight: 34, flex: '0 0 auto',
            borderBottom: `1px solid ${glassText(0.08)}`,
            '& .MuiTab-root': { minHeight: 34, fontSize: fs(11), color: glassText(0.6),
              textTransform: 'none', letterSpacing: 0.3, py: 0 },
            '& .Mui-selected': { color: `${glassText(1)} !important` },
            '& .MuiTabs-indicator': { backgroundColor: CHART_ANALYTICA_ACCENT },
          }}
        >
          {TABS.map(t => <Tab key={t.key} value={t.key} label={t.label} />)}
        </Tabs>

        <GuidanceContext.Provider value={guidance}>
        <Box sx={{ overflowY: 'auto', overflowX: 'hidden', px: 1.25, py: 1.25, flex: 1 }}>
          {!active && (
            <Typography sx={{ fontSize: fs(12), color: glassText(0.6) }}>
              Right-click a bar and choose Chart Analytica, or use the toolbar button, to scan
              from the oldest bar up to that one.
            </Typography>
          )}
          {active && !other && <ReportBody report={active.report} tab={tab} />}
          {active && other && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              {[active, other].map((run, i) => (
                <Box key={run.id} sx={{ minWidth: 0,
                  borderLeft: i === 1 ? `1px solid ${glassText(0.12)}` : 'none',
                  pl: i === 1 ? 2 : 0 }}>
                  <Typography sx={{ ...mono, fontSize: fs(11), fontWeight: 700,
                    color: CHART_ANALYTICA_ACCENT, mb: 1 }}>
                    {run.label}
                  </Typography>
                  <ReportBody report={run.report} tab={tab} />
                </Box>
              ))}
            </Box>
          )}
        </Box>
        </GuidanceContext.Provider>
      </Box>
      </Draggable>
    </Box>
  );
}

ChartAnalyticaDialog.propTypes = {
  open: PropTypes.bool,
  /** `[{id, label, report}]`, oldest first. Every run is kept — the history IS the comparison. */
  runs: PropTypes.array,
  activeId: PropTypes.string,
  onSelectRun: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  annotations: PropTypes.bool,
  onToggleAnnotations: PropTypes.func.isRequired,
};

ChartAnalyticaDialog.defaultProps = {
  open: false,
  runs: [],
  activeId: null,
  annotations: true,
};
